import "./App.css";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Footer from "./components/Website/Footer";
import Login from "./components/Login";
import Forgot from "./components/Forgot";
import Faculty from "./components/Teacher/Faculty";
import Student from "./components/Student/Student";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { login, logout } from "./redux/reducers/AuthSlice";
import { setRole } from "./redux/reducers/UserSlice";
import Loader from "./components/Loader";
import ResetPassword from "./components/ResetPass";
import Unauthorized from "./components/Website/Unauthorized";
import { Toaster } from "react-hot-toast";
import Department from "./components/Department/Department";
import { useThemeContext } from "./context/ThemeContext";
import FacultyOfficeOrders from "./components/Teacher/Pages/OfficeOrders";
import FacultyCircular from "./components/Teacher/Pages/CircularNotices";
import DepartmentCirculars from "./components/Department/Pages/CircularsNotices";
import API from "./utils/API";
import Home from "./components/Home/Home";
import ResearchDigestPage from "./Pages/ResearchDigestPage";
import EchoesDTU from "./components/InteractPage/Interact";
import "../src/App.css";
import ComingSoon from "./components/Website/ComingSoon";
import ExitSurvey from "./components/Student/Pages/ExitSurvey";
import Admin from "./components/Admin/Admin";
import COPOAttainmentTool from "./components/Teacher/CO-PO Mapping/FacultyAttainment";
import Alumni from "./components/Alumni/Alumni";
import ContactPage from "./components/Website/ContactUs";
import DepartmentsPage from "./components/Website/Know Your Faculty/Department";
import ScrollToTop from "./utils/ScrollToTop";
import AdminCOPO from "./components/Admin/CO-PO Mapping/AdminCoPo";

const CURRENT_VERSION = "2.4"; // Change this on every deployment
if (localStorage.getItem("appVersion") !== CURRENT_VERSION) {
  localStorage.clear(); // Clears old cache
  sessionStorage.clear();
  caches.keys().then((names) => {
    names.forEach((name) => caches.delete(name)); // Clear service worker cache
  });
  localStorage.setItem("appVersion", CURRENT_VERSION);
  window.location.reload(true); // Force reload to fetch fresh files
}

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { role } = useSelector((state) => state.user);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const location = useLocation();

  useEffect(() => {
    const checkSession = async () => {
      try {
        const currentRole = role || "student";
        const { data } = await API.get(`ece/${currentRole}/verify`);

        if (data.user) {
          dispatch(login({ user: data.user }));
          if (!role) {
            dispatch(setRole(data.user.position));
          }
        }
      } catch (err) {
        dispatch(logout());
        navigate("/login", { state: { from: location.pathname } });
      }
    };

    if (!isAuthenticated) {
      checkSession();
    }
  }, [location.pathname, dispatch, navigate, role, isAuthenticated]);

  if (!isAuthenticated) {
    return <Loader />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(role)) {
    return <Unauthorized />;
  }

  return children;
};

// Role-specific route components
const StudentRoute = ({ children }) => (
  <ProtectedRoute allowedRoles={["student"]}>{children}</ProtectedRoute>
);

const FacultyRoute = ({ children }) => (
  <ProtectedRoute allowedRoles={["faculty"]}>{children}</ProtectedRoute>
);

const AdminRoute = ({ children }) => (
  <ProtectedRoute allowedRoles={["admin"]}>{children}</ProtectedRoute>
);

const DepartmentRoute = ({ children }) => (
  <ProtectedRoute allowedRoles={["department"]}>{children}</ProtectedRoute>
);

const AlumniRoute = ({ children }) => (
  <ProtectedRoute allowedRoles={["alumni"]}>{children}</ProtectedRoute>
);
const AuthenticatedRoute = ({ children }) => (
  <ProtectedRoute>{children}</ProtectedRoute>
);

function App() {
  const { darkMode } = useThemeContext();

  useEffect(() => {
    document.body.style.backgroundColor = darkMode ? "#0D1117" : "#FFFFFF";
    document.body.style.color = darkMode ? "#EAEAEA" : "#000000";
  }, [darkMode]);

  return (
    <>
      <Toaster
        toastOptions={{
          className: "",
          duration: 2500,
          style: {
            background: "#1f2937",
            color: "#fff",
          },
        }}
      />
      <ScrollToTop />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot" element={<Forgot />} />
        <Route path="/attainment" element={<COPOAttainmentTool />} />

        <Route path="/contact-us" element={<ContactPage />} />
        <Route
          path="/student/reset-password/:token"
          element={<ResetPassword />}
        />
        <Route
          path="/faculty/reset-password/:token"
          element={<ResetPassword />}
        />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/research-digest" element={<ResearchDigestPage />} />
        <Route path="/departments" element={<DepartmentsPage />} />
        {/* Student Routes */}
        <Route
          path="/student"
          element={
            <StudentRoute>
              <Student />
            </StudentRoute>
          }
        />
        <Route
          path="/student/echoes"
          element={
            <StudentRoute>
              <EchoesDTU />
            </StudentRoute>
          }
        />
        <Route
          path="/student/alumni"
          element={
            <StudentRoute>
              <ComingSoon />
            </StudentRoute>
          }
        />
        <Route
          path="/student/exit-survey"
          element={
            <StudentRoute>
              <ExitSurvey />
            </StudentRoute>
          }
        />

        {/* Faculty Routes */}
        <Route
          path="/faculty"
          element={
            <FacultyRoute>
              <Faculty />
            </FacultyRoute>
          }
        />
        <Route
          path="/faculty/office-orders"
          element={
            <FacultyRoute>
              <FacultyOfficeOrders />
            </FacultyRoute>
          }
        />
        <Route
          path="/faculty/circular-notices"
          element={
            <FacultyRoute>
              <FacultyCircular />
            </FacultyRoute>
          }
        />
        <Route
          path="/faculty/echoes"
          element={
            <FacultyRoute>
              <EchoesDTU />
            </FacultyRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <Admin />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/co-po-attainment"
          element={
            <AdminRoute>
              <AdminCOPO />
            </AdminRoute>
          }
        />

        {/* Department Routes */}
        <Route
          path="/department"
          element={
            <DepartmentRoute>
              <Department />
            </DepartmentRoute>
          }
        />
        <Route
          path="/department/office-orders"
          element={
            <DepartmentRoute>
              <Department />
            </DepartmentRoute>
          }
        />
        <Route
          path="/department/circular-notices"
          element={
            <DepartmentRoute>
              <DepartmentCirculars />
            </DepartmentRoute>
          }
        />

        {/* Authenticated but not role-specific routes */}

        <Route
          path="/alumni"
          element={
            <AlumniRoute>
              <Alumni />
            </AlumniRoute>
          }
        />
        <Route
          path="/alumni/echoes"
          element={
            <AlumniRoute>
              <EchoesDTU />
            </AlumniRoute>
          }
        />

        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      <Footer />
    </>
  );
}

export default App;
